import { useState, useEffect, useRef, useMemo } from "react";
import {
  Sparkles,
  CheckCircle2,
  Copy,
  ExternalLink,
  Check,
  Globe,
} from "lucide-react";
import type { ProposalLocale } from "../../proposal-standard/i18n/types";
import {
  SUPPORTED_LOCALES,
  LOCALE_LABELS,
  LOCALE_FLAGS,
} from "../../proposal-standard/i18n/types";
import { Button } from "@/components/ui/button";
import { createToast } from "vercel-toast";
import { useAuth } from "@/features/authentication";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { reportError } from "@/utils/observability";
import { visionService } from "@/services/vision-service";
import {
  DEFAULT_CONTRACT_TERM,
  DEFAULT_LICENSE_FEE_MODE,
  DEFAULT_LICENSE_FEE_PERCENTAGE,
  type AIModeState,
} from "../types";
import { calculateLicenseFeeFromAdjustments } from "../utils/licenseFee";
import { buildProposalPayload } from "../utils/buildProposalPayload";
import { getProposalUrl } from "../../utils/proposal-url";

function AnimatedDots() {
  const [count, setCount] = useState(1);
  useEffect(() => {
    const id = setInterval(() => setCount((c) => (c % 3) + 1), 500);
    return () => clearInterval(id);
  }, []);
  return (
    <span
      style={{ display: "inline-block", minWidth: "1.5ch", textAlign: "left" }}
    >
      {".".repeat(count)}
    </span>
  );
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fallback below
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

interface ProposalStepProps {
  state: AIModeState;
  onValidityChange?: (valid: boolean) => void;
  onError?: () => void;
  onStartNewSearch?: () => void;
}

export default function ProposalStep({
  state,
  onValidityChange,
  onError,
  onStartNewSearch,
}: ProposalStepProps) {
  const [isGenerating, setIsGenerating] = useState(true);
  const [generated, setGenerated] = useState(false);
  const [proposalUrl, setProposalUrl] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [selectedLang, setSelectedLang] = useState<ProposalLocale>("pt");
  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const onValidityChangeRef = useRef(onValidityChange);
  onValidityChangeRef.current = onValidityChange;
  const hasFiredRef = useRef(false);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current)
        clearTimeout(copyFeedbackTimeoutRef.current);
    };
  }, []);

  const proposalUrlWithLang = useMemo(() => {
    if (!proposalUrl) return "";
    if (selectedLang === "pt") return proposalUrl;
    return `${proposalUrl}?lang=${selectedLang}`;
  }, [proposalUrl, selectedLang]);

  const { userInfo } = useAuth();
  const { diagnosis, location, adjustments } = state;

  const selectedSpaceIds = location?.selectedSpaceIds ?? [];
  const selectedSpaces =
    adjustments?.selectedSpaces ??
    (location?.rankedSpaces ?? []).filter((s) =>
      selectedSpaceIds.includes(s.id),
    );

  const spaceAdjustments = adjustments?.spaceAdjustments ?? [];

  const spacesWithMonthly = spaceAdjustments.filter((a) => a.totalPrice > 0);
  const avgMonthlyPrice =
    spacesWithMonthly.length > 0
      ? spacesWithMonthly.reduce((s, a) => s + a.totalPrice, 0) /
        spacesWithMonthly.length
      : 0;

  const licenseFeeCalc = useMemo(() => {
    const adjs = adjustments?.spaceAdjustments ?? [];
    return calculateLicenseFeeFromAdjustments(
      adjustments?.licenseFeeMode ?? DEFAULT_LICENSE_FEE_MODE,
      adjustments?.licenseFeePercentage ?? DEFAULT_LICENSE_FEE_PERCENTAGE,
      adjustments?.licenseFeeFixed ?? 0,
      adjs,
      adjustments?.contractMonths ?? DEFAULT_CONTRACT_TERM,
    );
  }, [adjustments]);

  const avgSetupCost = licenseFeeCalc.value;

  const avgTotal = avgMonthlyPrice > 0 ? avgMonthlyPrice : 0;

  const validityDate = adjustments?.proposalValidity
    ? new Date(adjustments.proposalValidity).toLocaleDateString("pt-BR")
    : "—";

  useEffect(() => {
    if (hasFiredRef.current) return;
    hasFiredRef.current = true;

    onValidityChangeRef.current?.(false);

    async function generate() {
      const payload = buildProposalPayload(state, userInfo);

      let url: string;
      let proposalId: string;

      try {
        const [result] = await Promise.all([
          visionService.createProposal(payload),
          new Promise((resolve) => setTimeout(resolve, 4000)),
        ]);
        proposalId = result.uuid;
        url = getProposalUrl(proposalId);

        const dealId = state.diagnosis?.dealId;
        const userId = userInfo?.user?.id;
        if (dealId && userId) {
          visionService
            .completeTtoMetric(dealId, String(userId))
            .catch((error) => {
              reportError(error, {
                operation: "completeTtoMetric",
                step: "proposal",
                dealId,
              });
            });
        }
      } catch (error) {
        reportError(error, {
          operation: "createProposal",
          step: "proposal",
          dealId: state.diagnosis?.dealId,
        });
        createToast("Erro ao gerar a proposta. Tente novamente mais tarde.", {
          type: "error",
          timeout: 5000,
        });
        onError?.();
        return;
      }

      try {
        localStorage.setItem(
          `vision_proposal_${proposalId}`,
          JSON.stringify({
            uuid: proposalId,
            state,
            generatedAt: new Date().toISOString(),
            selectedSpaces,
          }),
        );
      } catch {
        // ignore
      }

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url).catch(() => {});
      }

      sendTrackDataServer({
        eventName: "VISION_AI_MODE_PROPOSAL_GENERATED",
        productType: "PO",
        url: window.location.pathname,
        data: {
          deal_id: diagnosis?.dealId ?? null,
          user_id: userInfo?.user?.id ?? null,
          email: userInfo?.profile?.email ?? null,
          company_name: diagnosis?.companyName ?? null,
          proposal_uuid: proposalId,
          proposal_url: url,
          selected_spaces_count: selectedSpaceIds.length,
          selected_space_ids: selectedSpaceIds,
          contract_months: adjustments?.contractMonths ?? null,
          avg_monthly_price: Math.round(avgMonthlyPrice),
          avg_setup_cost: Math.round(avgSetupCost),
          avg_total: Math.round(avgTotal),
          rooms_with_compliance_badge_count: selectedSpaces.filter(
            (s) => s.spaceHasAvcb || s.spaceHasOperatingPermit,
          ).length,
        },
      });

      setProposalUrl(url);
      setIsGenerating(false);
      setGenerated(true);
      onValidityChangeRef.current?.(true);
    }

    generate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function copyLink() {
    const didCopy = await copyToClipboard(proposalUrlWithLang);
    if (!didCopy) {
      createToast("Não foi possível copiar o link. Tente novamente.", {
        type: "error",
        timeout: 3000,
      });
      return;
    }
    if (copyFeedbackTimeoutRef.current)
      clearTimeout(copyFeedbackTimeoutRef.current);
    setLinkCopied(true);
    copyFeedbackTimeoutRef.current = setTimeout(() => {
      setLinkCopied(false);
      copyFeedbackTimeoutRef.current = null;
    }, 3000);
    sendTrackDataServer({
      eventName: "VISION_AI_MODE_PROPOSAL_LINK_COPIED",
      productType: "PO",
      url: window.location.pathname,
      data: {
        deal_id: diagnosis?.dealId ?? null,
        user_id: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
        company_name: diagnosis?.companyName ?? null,
        proposal_url: proposalUrlWithLang,
      },
    });
  }

  function openProposal() {
    sendTrackDataServer({
      eventName: "VISION_AI_MODE_PROPOSAL_OPENED",
      productType: "PO",
      url: window.location.pathname,
      data: {
        deal_id: diagnosis?.dealId ?? null,
        user_id: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
        company_name: diagnosis?.companyName ?? null,
        proposal_url: proposalUrlWithLang,
      },
    });
    window.open(proposalUrlWithLang, "_blank");
  }

  const summaryRows = [
    { label: "Cliente", value: diagnosis?.companyName ?? "—" },
    {
      label: "Localização",
      value: diagnosis?.targetCity ?? location?.location?.label ?? "—",
    },
    {
      label: "Posições",
      value: (() => {
        const min =
          location?.capacityMin ??
          (diagnosis?.positionsMin !== "" ? diagnosis?.positionsMin : null);
        const max =
          location?.capacityMax ??
          (diagnosis?.positionsMax !== "" ? diagnosis?.positionsMax : null);
        if (min != null && max != null) return `${min} – ${max}`;
        if (min != null) return `${min}+`;
        if (max != null) return `até ${max}`;
        return "—";
      })(),
    },
    {
      label: "Salas na proposta",
      value: `${selectedSpaces.length} sala${selectedSpaces.length !== 1 ? "s" : ""}`,
    },
    {
      label: "Prazo do contrato",
      value: `${adjustments?.contractMonths ?? DEFAULT_CONTRACT_TERM} meses`,
    },
    {
      label: "Mensalidade média",
      value:
        avgMonthlyPrice > 0
          ? `R$ ${Math.round(avgMonthlyPrice).toLocaleString("pt-BR")}`
          : "—",
    },
    {
      label: "License Fee",
      value:
        adjustments?.showLicenseFee && licenseFeeCalc.value > 0
          ? `R$ ${licenseFeeCalc.value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })} (pago ${licenseFeeCalc.period === "semiannual" ? "semestralmente" : "anualmente"})`
          : "—",
    },
    {
      label: "Setup Fee",
      value:
        adjustments?.includeSetupFee && (adjustments?.setupFee ?? 0) > 0
          ? `R$ ${(adjustments.setupFee as number).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} (pago uma única vez)`
          : "—",
    },
    {
      label: "Total médio",
      value:
        avgTotal > 0
          ? `R$ ${Math.round(avgTotal).toLocaleString("pt-BR")}`
          : "—",
    },
    { label: "Validade", value: validityDate },
  ];

  return (
    <div className="space-y-6">
      <div>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-4"
          style={{
            background: "rgba(255,217,0,0.1)",
            border: "1px solid rgba(255,217,0,0.25)",
            color: "#1A1814",
          }}
        >
          <Sparkles className="w-3.5 h-3.5" />
          Proposta
        </span>
        <h2
          className="text-2xl font-bold mt-4 mb-1"
          style={{ color: "#1A1814" }}
        >
          {generated ? (
            "Proposta gerada!"
          ) : isGenerating ? (
            <>
              Gerando proposta
              <AnimatedDots />
            </>
          ) : (
            "Gerar Proposta"
          )}
        </h2>
        <p className="text-sm" style={{ color: "#78736D" }}>
          {generated
            ? "Compartilhe com o cliente"
            : isGenerating
              ? "Aguarde enquanto preparamos tudo"
              : "Revise o resumo e gere o link da proposta"}
        </p>
      </div>

      {generated && (
        <div
          className="space-y-3"
          style={{ animation: "aiStepEnter 0.4s cubic-bezier(0.16,1,0.3,1)" }}
        >
          <div
            className="rounded-2xl p-5 text-center"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,217,0,0.09), rgba(255,217,0,0.02))",
              border: "1.5px solid rgba(255,217,0,0.22)",
            }}
          >
            <div
              className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-3"
              style={{
                background: "#FFD900",
                animation: "successBounce 0.55s cubic-bezier(0.16,1,0.3,1)",
              }}
            >
              <CheckCircle2 className="w-7 h-7" style={{ color: "#1A1814" }} />
            </div>
            <p
              className="text-sm font-semibold mb-0.5"
              style={{ color: "#1A1814" }}
            >
              Proposta para{" "}
              <span style={{ fontStyle: "italic" }}>
                {diagnosis?.companyName ?? "cliente"}
              </span>{" "}
              {"gerada"}
            </p>
            <p className="text-xs mb-3" style={{ color: "#78736D" }}>
              Link copiado para área de transferência
            </p>
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold"
              style={{ background: "rgba(124,58,237,0.1)", color: "#7C3AED" }}
            >
              <Sparkles className="w-3 h-3" />
              Otimizada por AI
            </span>
          </div>

          {/* Language selector */}
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-xl"
            style={{ background: "#F5F3EF", border: "1px solid #E8E6E3" }}
          >
            <div className="flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" style={{ color: "#78736D" }} />
              <span
                className="text-xs font-medium"
                style={{ color: "#78736D" }}
              >
                Idioma da proposta
              </span>
            </div>
            <div
              className="ml-auto inline-flex rounded-lg overflow-hidden"
              style={{ border: "1px solid #E8E6E3" }}
            >
              {SUPPORTED_LOCALES.map((loc) => (
                <button
                  key={loc}
                  type="button"
                  onClick={() => {
                    setSelectedLang(loc);
                    setLinkCopied(false);
                  }}
                  className="px-3 py-1.5 text-xs font-semibold transition-all"
                  style={{
                    background: selectedLang === loc ? "#1A1814" : "white",
                    color: selectedLang === loc ? "white" : "#78736D",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  {LOCALE_FLAGS[loc]} {LOCALE_LABELS[loc]}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Button
              type="button"
              onClick={copyLink}
              className="w-full gap-1.5"
              style={
                linkCopied
                  ? {
                      background: "#FFD900",
                      color: "#1A1814",
                      borderColor: "#FFD900",
                    }
                  : {
                      background: "#1A1814",
                      color: "white",
                      borderColor: "#1A1814",
                    }
              }
            >
              {linkCopied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copiado com sucesso
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copiar Link da Proposta
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={openProposal}
              className="w-full gap-1.5"
            >
              <ExternalLink className="w-4 h-4" />
              Visualizar como cliente
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onStartNewSearch?.()}
              className="w-full gap-1.5 border-[#E8E6E3] hover:border-[#1A1814]"
            >
              Nova busca
            </Button>
          </div>
        </div>
      )}

      {isGenerating && (
        <div className="text-center py-6">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background: "rgba(255,217,0,0.2)",
                animation: "radarPing 1.8s ease-out infinite",
              }}
            />
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background: "rgba(255,217,0,0.14)",
                animation: "radarPing 1.8s ease-out infinite 0.65s",
              }}
            />
            <div
              className="absolute inset-0 rounded-full flex items-center justify-center"
              style={{ background: "rgba(255,217,0,0.1)" }}
            >
              <Sparkles
                className="w-6 h-6 animate-pulse"
                style={{ color: "#FFD900" }}
              />
            </div>
          </div>
          <p
            className="text-sm font-medium mb-0.5"
            style={{ color: "#1A1814" }}
          >
            Gerando proposta
            <AnimatedDots />
          </p>
          <p className="text-xs" style={{ color: "#9E9A94" }}>
            Otimizando com inteligência artificial
          </p>
        </div>
      )}

      <div
        className="rounded-2xl overflow-hidden"
        style={{ border: "1px solid #E8E6E3" }}
      >
        <div
          className="px-4 py-3 flex items-center justify-between relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #FFD900 0%, #FFE94D 60%, #FFF3A3 100%)",
            borderBottom: "1px solid rgba(255,217,0,0.3)",
          }}
        >
          <div
            className="absolute inset-y-0 pointer-events-none"
            style={{
              width: "40%",
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
              animation: "shimmerSlide 2.8s ease-in-out 1s infinite",
            }}
          />
          <div className="flex items-center gap-2">
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: "rgba(26,24,20,0.12)" }}
            >
              <Sparkles className="w-3 h-3" style={{ color: "#1A1814" }} />
            </div>
            <span
              className="text-xs font-bold uppercase tracking-wider"
              style={{ color: "#1A1814" }}
            >
              Resumo da proposta
            </span>
          </div>
        </div>

        <div>
          {summaryRows.map((row, i) => (
            <div
              key={row.label}
              className="flex items-center justify-between px-4 py-2.5"
              style={{
                borderBottom:
                  i < summaryRows.length - 1 ? "1px solid #F0EEEB" : undefined,
              }}
            >
              <span className="text-xs" style={{ color: "#78736D" }}>
                {row.label}
              </span>
              <span
                className="text-xs font-medium"
                style={{ color: "#1A1814" }}
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
